/**
 * useNodeHighlighting Hook
 * Node-ok vizuális kiemelése drag & drop közben
 */

import { useCallback, useState } from 'react';
import { Node } from 'reactflow';
import { FlowNode, FlowNodeType } from '../types/flowBuilder';
import { useNodeConnectionValidation } from './useNodeConnectionValidation';

export interface NodeHighlightState {
  compatibleNodes: Set<string>;
  incompatibleNodes: Set<string>;
  sourceNodeId: string | null;
}

export const useNodeHighlighting = (nodes: Node[], edges: any[]) => {
  const { getCompatibleNodeTypes } = useNodeConnectionValidation();
  const [highlightState, setHighlightState] = useState<NodeHighlightState>({
    compatibleNodes: new Set(),
    incompatibleNodes: new Set(),
    sourceNodeId: null
  });

  /**
   * Elindítja a kiemelést amikor egy node-ból húzunk
   */
  const startHighlighting = useCallback(
    (sourceNodeId: string) => {
      const sourceNode = nodes.find((n) => n.id === sourceNodeId) as FlowNode;
      if (!sourceNode) return;

      // Kompatibilis node típusok lekérése
      const compatibleTypes = getCompatibleNodeTypes(
        sourceNode.type as FlowNodeType,
        edges,
        sourceNodeId
      );

      const compatible = new Set<string>();
      const incompatible = new Set<string>();

      // Minden node kategorizálása
      nodes.forEach((node) => {
        if (node.id === sourceNodeId) return; // Forrás node kihagyása

        const targetNode = node as FlowNode;
        const isCompatible = compatibleTypes.includes(targetNode.type as FlowNodeType);

        if (isCompatible) {
          compatible.add(node.id);
        } else {
          incompatible.add(node.id);
        }
      });

      setHighlightState({
        compatibleNodes: compatible,
        incompatibleNodes: incompatible,
        sourceNodeId
      });
    },
    [nodes, edges, getCompatibleNodeTypes]
  );

  /**
   * Leállítja a kiemelést
   */
  const stopHighlighting = useCallback(() => {
    setHighlightState({
      compatibleNodes: new Set(),
      incompatibleNodes: new Set(),
      sourceNodeId: null
    });
  }, []);

  /**
   * Ellenőrzi, hogy egy node kompatibilis-e
   */
  const isNodeCompatible = useCallback(
    (nodeId: string): boolean => {
      return highlightState.compatibleNodes.has(nodeId);
    },
    [highlightState]
  );

  /**
   * Ellenőrzi, hogy egy node inkompatibilis-e
   */
  const isNodeIncompatible = useCallback(
    (nodeId: string): boolean => {
      return highlightState.incompatibleNodes.has(nodeId);
    },
    [highlightState]
  );

  /**
   * Visszaadja a node kiemelési stílusát
   */
  const getNodeHighlightStyle = useCallback(
    (nodeId: string): React.CSSProperties => {
      if (highlightState.sourceNodeId === nodeId) {
        // Forrás node (kék)
        return {
          boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.5)',
          borderColor: '#3B82F6',
          borderWidth: '2px'
        };
      }

      if (highlightState.compatibleNodes.has(nodeId)) {
        // Kompatibilis node (zöld)
        return {
          boxShadow: '0 0 0 3px rgba(34, 197, 94, 0.5)',
          borderColor: '#22C55E',
          borderWidth: '2px',
          backgroundColor: 'rgba(34, 197, 94, 0.1)'
        };
      }

      if (highlightState.incompatibleNodes.has(nodeId)) {
        // Inkompatibilis node (piros)
        return {
          boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.3)',
          borderColor: '#EF4444',
          borderWidth: '2px',
          backgroundColor: 'rgba(239, 68, 68, 0.05)',
          opacity: 0.6
        };
      }

      return {};
    },
    [highlightState]
  );

  /**
   * Visszaadja a node kiemelési CSS osztályát
   */
  const getNodeHighlightClass = useCallback(
    (nodeId: string): string => {
      const classes: string[] = [];

      if (highlightState.sourceNodeId === nodeId) {
        classes.push('node-source-active');
      }

      if (highlightState.compatibleNodes.has(nodeId)) {
        classes.push('node-compatible');
      }

      if (highlightState.incompatibleNodes.has(nodeId)) {
        classes.push('node-incompatible');
      }

      return classes.join(' ');
    },
    [highlightState]
  );

  return {
    highlightState,
    startHighlighting,
    stopHighlighting,
    isNodeCompatible,
    isNodeIncompatible,
    getNodeHighlightStyle,
    getNodeHighlightClass
  };
};
